// ---------------- EmailContestar.jsx -----------------

import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  Select,
  MenuItem,
  Chip,
  Typography,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";

import DropZoneWrapper from "../dropwrapper/DropZoneWrapper";
import TipTapEditorContestar from "./TipTapEditorContestar";
import axios from "axios";

const API = process.env.REACT_APP_API_URL;

const ACCENT = "#00bbcd";
const ACCENT_HOVER = "#00a5b5";
const ON_ACCENT = "#0f172a";

const accentBtnSx = {
  bgcolor: ACCENT,
  color: ON_ACCENT,
  fontWeight: 600,
  boxShadow: "none",
  "&:hover": { bgcolor: ACCENT_HOVER, boxShadow: "none" },
};

const accentOutlinedSx = {
  color: ACCENT,
  borderColor: ACCENT,
  "&:hover": {
    borderColor: ACCENT_HOVER,
    bgcolor: "rgba(0, 187, 205, 0.08)",
  },
};

const accentTextSx = {
  color: ACCENT,
  "&:hover": { bgcolor: "rgba(0, 187, 205, 0.08)" },
};

export default function EmailContestar({
  open,
  onClose,
  onSend,
  email,
  accounts,
  originalHtml,
}) {
  const editorRef = useRef(null);

  const [fromEmail, setFromEmail] = useState("");
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");

  const [selectedSignature, setSelectedSignature] = useState("main");
  const [signatures, setSignatures] = useState({ main: "", reply: "" });

  const [attachments, setAttachments] = useState([]);
  const [initialHtml, setInitialHtml] = useState("");
  const [isSending, setIsSending] = useState(false);

  /* -----------------------------------------------------
        DECODE BASE64 PARA ADJUNTOS ORIGINALES
  ----------------------------------------------------- */
  function decodeBase64Safe(b64) {
    if (!b64 || typeof b64 !== "string") return null;
    let base64 = b64.replace(/-/g, "+").replace(/_/g, "/");
    const padding = 4 - (base64.length % 4);
    if (padding !== 4) base64 += "=".repeat(padding);

    try {
      const raw = atob(base64);
      return Uint8Array.from(raw, (c) => c.charCodeAt(0));
    } catch {
      return null;
    }
  }

  const [originalAttachments, setOriginalAttachments] = useState([]);

  const closeDialogSafely = () => {
    onClose?.();
    // Refuerzo por si hay un re-render intermedio del padre.
    requestAnimationFrame(() => onClose?.());
    setTimeout(() => onClose?.(), 180);
  };

  /* -----------------------------------------------------
        RESET AL ABRIR
  ----------------------------------------------------- */
  useEffect(() => {
    if (!open || !email) return;

    setAttachments([]);

    setTo(email.from || "");
    setSubject(`Re: ${email.subject || ""}`);

    if (accounts?.length > 0) {
      setFromEmail(accounts[0].email.toLowerCase());
    }
  }, [open, email, accounts]);

  /* -----------------------------------------------------
        CARGAR FIRMAS
  ----------------------------------------------------- */
  useEffect(() => {
    if (!open) return;
    if (!fromEmail) return;

    axios.get(`${API}/signatures/${fromEmail}`).then((res) => {
      const main = res.data.signature_html || "";
      const reply = res.data.reply_signature_html || main;
      setSignatures({ main, reply });
    });
  }, [open, fromEmail]);

  /* -----------------------------------------------------
        PREPARAR HTML INICIAL
  ----------------------------------------------------- */
  useEffect(() => {
    if (!open || !originalHtml) return;

    const signatureHTML =
      selectedSignature === "main"
        ? signatures.main
        : selectedSignature === "reply"
        ? signatures.reply
        : "";

    const repliedBlock = `
      <br/><br/>
      ---------- Mensaje original ----------
      <br/><b>De:</b> ${email.from}<br/>
      <b>Fecha:</b> ${new Date(email.date).toLocaleString("es-ES")}<br/>
      <b>Asunto:</b> ${email.subject}<br/><br/>
      ${originalHtml}
    `;

    const html = `${signatureHTML}${repliedBlock}`;
    setInitialHtml(html);
  }, [open, signatures, selectedSignature, originalHtml, email]);

  /* -----------------------------------------------------
        CARGAR ADJUNTOS ORIGINALES
  ----------------------------------------------------- */
  useEffect(() => {
    if (!open || !email) return;

    axios
      .get(`${API}/emails/${email.gmailId}/attachments/full`)
      .then((res) => {
        const converted = res.data
          .map((a) => {
            const arr = decodeBase64Safe(a.dataBase64);
            if (!arr) return null;

            return new File([arr], a.filename || "archivo", {
              type: a.mimeType || "application/octet-stream",
            });
          })
          .filter(Boolean);

        setOriginalAttachments(converted);
      })
      .catch(() => setOriginalAttachments([]));
  }, [open, email]);

  /* -----------------------------------------------------
        MANEJO DE NUEVOS ARCHIVOS
  ----------------------------------------------------- */
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setAttachments((prev) => [...prev, ...files]);
  };

  const removeAttachment = (name) => {
    setAttachments((prev) => prev.filter((f) => f.name !== name));
  };

  /* -----------------------------------------------------
        ENVIAR RESPUESTA
  ----------------------------------------------------- */
  const sendReply = async () => {
    if (isSending) return;
    if (!to.trim()) {
      alert("Debe indicar un destinatario.");
      return;
    }

    const html = editorRef.current.getHTML();

    const form = new FormData();
    form.append("fromEmail", fromEmail);
    form.append("to", to);
    form.append("subject", subject);
    form.append("html", html);

    // adjuntos nuevos
    attachments.forEach((file) => form.append("attachments", file));

    // adjuntos originales
    originalAttachments.forEach((file) => form.append("attachments", file));

    setIsSending(true);
    const sendPromise = Promise.resolve(onSend(form));
    closeDialogSafely();
    try {
      await sendPromise;
    } catch (err) {
      console.error("Error enviando respuesta:", err);
    } finally {
      closeDialogSafely();
      setIsSending(false);
    }
  };

  /* -----------------------------------------------------
        UI
  ----------------------------------------------------- */
  return (
    <Dialog open={open} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
        Responder mensaje
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        {/* FROM */}
        <Select
          fullWidth
          size="small"
          sx={{ mb: 2 }}
          value={fromEmail}
          onChange={(e) => setFromEmail(e.target.value)}
        >
          {accounts.map((acc) => (
            <MenuItem key={acc.id} value={acc.email.toLowerCase()}>
              {acc.email}
            </MenuItem>
          ))}
        </Select>

        {/* PARA */}
        <TextField
          fullWidth
          size="small"
          label="Para"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          sx={{ mb: 2 }}
        />

        {/* ASUNTO */}
        <TextField
          fullWidth
          size="small"
          label="Asunto"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          sx={{ mb: 2 }}
        />

        {/* SELECT FIRMA */}
        <Select
          fullWidth
          size="small"
          sx={{ mb: 2 }}
          value={selectedSignature}
          onChange={(e) => setSelectedSignature(e.target.value)}
        >
          <MenuItem value="reply">Firma de respuesta</MenuItem>
          <MenuItem value="main">Firma principal</MenuItem>
          <MenuItem value="none">Sin firma</MenuItem>
        </Select>

        {/* BOTÓN ADJUNTAR */}
        <Button
          variant="outlined"
          size="small"
          startIcon={<AttachFileIcon />}
          component="label"
          sx={{ mb: 1, ...accentOutlinedSx }}
        >
          Adjuntar archivos
          <input type="file" hidden multiple onChange={handleFileChange} />
        </Button>

        {/* Adjuntos originales */}
        {originalAttachments.length > 0 && (
          <Box sx={{ mt: 1, mb: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
            {originalAttachments.map((file) => (
              <Chip
                key={file.name}
                label={`📎 ${file.name}`}
                onDelete={() =>
                  setOriginalAttachments((prev) =>
                    prev.filter((f) => f.name !== file.name)
                  )
                }
              />
            ))}
          </Box>
        )}

        {/* Adjuntos nuevos */}
        {attachments.length > 0 && (
          <Box sx={{ mt: 1, mb: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
            {attachments.map((file) => (
              <Chip
                key={file.name}
                label={file.name}
                onDelete={() => removeAttachment(file.name)}
              />
            ))}
          </Box>
        )}

        {/* EDITOR */}
        {initialHtml ? (
          <DropZoneWrapper
            onFiles={(files) => setAttachments((prev) => [...prev, ...files])}
          >
            <TipTapEditorContestar
              editorRef={editorRef}
              initialHtml={initialHtml}
              onChange={() => {}}
            />
          </DropZoneWrapper>
        ) : (
          <Typography sx={{ color: "#777" }}>Cargando…</Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSending} sx={accentTextSx}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={sendReply}
          disabled={isSending}
          sx={accentBtnSx}
        >
          {isSending ? "Enviando..." : "Enviar respuesta"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
